import { DOMParser as PM_DOMParser, Slice } from '@tiptap/pm/model'
import { Plugin, PluginKey } from '@tiptap/pm/state'
import type { EditorView } from '@tiptap/pm/view'
import { Decoration, DecorationSet } from '@tiptap/pm/view'

import { createUploadPlaceholderWidget } from '../createUploadPlaceholderWidget'
import { processSliceRemoveImages } from './helpers'
import { isPlaceholderMeta, type ParseImagePluginParams } from './types'
import { UploadImageManager } from './UploadImageManager'

export const UPLOAD_IMAGE_PLUGIN_KEY = new PluginKey<DecorationSet>('upload-image')
const DEFAULT_MAX_SIZE = 10 * 1024 * 1024

// 处理各种本地添加图片逻辑
export const ParseImagePlugin = ({ uploadOptions, appId, tokenRefreshManager, maxSize }: ParseImagePluginParams) => {
    const uploadImageManager = new UploadImageManager(
        uploadOptions,
        tokenRefreshManager,
        maxSize ?? DEFAULT_MAX_SIZE,
        UPLOAD_IMAGE_PLUGIN_KEY
    )

    let pendingUpload: { files: File[]; pos: number } | null = null

    return new Plugin<DecorationSet>({
        key: UPLOAD_IMAGE_PLUGIN_KEY,

        state: {
            init() {
                return DecorationSet.empty
            },
            apply(tr, set) {
                set = set.map(tr.mapping, tr.doc)

                const meta = tr.getMeta(UPLOAD_IMAGE_PLUGIN_KEY)
                uploadImageManager.syncPlaceholderPositionMap(tr, meta)

                if (Array.isArray(meta)) {
                    pendingUpload = { files: [...meta], pos: tr.selection.from }
                    return set
                }

                if (!isPlaceholderMeta(meta)) {
                    return set
                }

                if (meta.type === 'addMany') {
                    meta.items.forEach(({ id, pos, side }) => {
                        const widget = createUploadPlaceholderWidget('图片上传中...')
                        const deco = Decoration.widget(pos, widget, { id, key: id, side: side ?? 0 })
                        set = set.add(tr.doc, [deco])
                    })
                } else if (meta.type === 'add') {
                    const widget = createUploadPlaceholderWidget('图片上传中...')
                    const deco = Decoration.widget(meta.pos, widget, { id: meta.id, key: meta.id, side: 0 })
                    set = set.add(tr.doc, [deco])
                } else if (meta.type === 'remove') {
                    const match = set.find(undefined, undefined, (spec: { id?: string }) => spec.id === meta.id)
                    set = set.remove(match)
                }

                return set
            }
        },

        props: {
            decorations(state) {
                return this.getState(state)
            },
            handleDOMEvents: {
                paste(view, event) {
                    const items = [...(event.clipboardData?.items ?? [])].filter(item => /image/i.test(item.type))
                    const files = items.map(item => item.getAsFile()).filter(Boolean) as File[]
                    if (files.length > 0) {
                        event.preventDefault()
                        uploadImageManager.createDecorationPlaceholdersAndUpload(view, files, view.state.selection.from)
                        return false
                    }

                    const html = event.clipboardData?.getData?.('text/html')
                    if (!html || !uploadOptions) {
                        return false
                    }

                    let slice: Slice | null = null
                    const parser = view.props.clipboardParser as
                        | ((view: EditorView, data: DataTransfer, event: ClipboardEvent) => Slice)
                        | undefined
                    if (typeof parser === 'function') {
                        slice = parser(view, event.clipboardData!, event)
                    } else if (typeof window !== 'undefined' && window.DOMParser) {
                        const doc = new window.DOMParser().parseFromString(html, 'text/html')
                        if (doc?.body) {
                            slice = PM_DOMParser.fromSchema(view.state.schema).parseSlice(doc.body)
                        }
                    }
                    if (!slice) {
                        return false
                    }

                    const { newSlice, slots } = processSliceRemoveImages(slice, appId)
                    if (slots.length === 0) {
                        return false
                    }

                    event.preventDefault()
                    const from = view.state.selection.from
                    uploadImageManager.pasteHtmlWithDecorationPlaceholders(view, newSlice, slots, from)
                    return false
                },

                drop(view, event) {
                    const hasFiles = event.dataTransfer?.files && event.dataTransfer.files.length > 0
                    if (!hasFiles) {
                        return false
                    }

                    const files = [...event.dataTransfer.files].filter(file => /image/i.test(file.type))
                    if (files.length === 0) {
                        return false
                    }

                    const coordinates = view.posAtCoords({
                        left: event.clientX,
                        top: event.clientY
                    })
                    if (!coordinates || !uploadOptions) {
                        return false
                    }

                    event.preventDefault()
                    uploadImageManager.createDecorationPlaceholdersAndUpload(view, files, coordinates.pos)
                    return false
                }
            }
        },

        view() {
            return {
                update(view) {
                    if (pendingUpload) {
                        const { files, pos } = pendingUpload
                        pendingUpload = null
                        queueMicrotask(() => {
                            uploadImageManager.createDecorationPlaceholdersAndUpload(view, files, pos)
                        })
                    }
                }
            }
        }
    })
}
